import { zodResolver } from '@hookform/resolvers/zod';
import { type Transaction } from '@kalos/kalos-rpc';
import { useForm, useFormContext } from 'react-hook-form';
import { z } from 'zod';

import { castToNumber } from '../../../../../tools/schemas';
import { type ZodObjectWithModelKeys } from '../../../../../tools/typeguargs';

export type TransactionCreateFields = Pick<
  Transaction,
  | 'timestamp'
  | 'orderNumber'
  | 'invoiceNumber'
  | 'ownerId'
  | 'departmentId'
  | 'jobId'
  | 'costCenterId'
  | 'amount'
  | 'vendorId'
  | 'notes'
  | 'paymentDueDate'
>;

export const transactionCreateScheme = z.object({
  timestamp: z
    .date()
    .optional()
    .default(() => new Date()),
  orderNumber: z.string().optional(),
  invoiceNumber: z.string().optional().default(''),
  ownerId: castToNumber.optional().default(0),
  departmentId: castToNumber.optional().default(0),
  jobId: z.number().optional().default(0),
  costCenterId: castToNumber.optional().default(0),
  // refine is to make sure that number has only 2 digits after comma
  amount: castToNumber
    .optional()
    .default(0)
    .refine(
      (x) => (x.toString().split('.')[1] || '').length <= 2,
      'Amount should have only 2 digits after comma',
    ),
  vendorId: castToNumber.optional().default(0),
  notes: z.string().optional().default(''),
  paymentDueDate: z.string().optional().default(''),
}) satisfies ZodObjectWithModelKeys<TransactionCreateFields>;

export type TransactionCreateSchemeType = z.infer<typeof transactionCreateScheme>;

export const getDefaultTransactionCreateFields = () => ({
  timestamp: new Date(),
  orderNumber: '',
  invoiceNumber: '',
  ownerId: 0,
  departmentId: 0,
  jobId: 0,
  costCenterId: 0,
  amount: 0,
  vendorId: 0,
  notes: '',
  paymentDueDate: '',
});

export const useTransactionCreateForm = () => {
  return useForm<TransactionCreateSchemeType>({
    resolver: zodResolver(transactionCreateScheme),
    mode: 'onSubmit',
    defaultValues: getDefaultTransactionCreateFields(),
  });
};

export const useTransactionCreateFormContext = () => {
  return useFormContext<TransactionCreateSchemeType>();
};
